// Gift voucher document template — A5 landscape, front + reverse.
// Shared by the admin Print view (browser) and the PDF endpoint (headless Chromium).

const escapeHtml = (str) => String(str || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const voucherDate = (dateStr) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '')
  return match ? `${match[3]}.${match[2]}.${match[1]}` : (dateStr || '')
}

const guestsLabel = (guests) => {
  if (guests === 1) return 'for One'
  if (guests === 2) return 'for Two'
  return `for ${guests} Guests`
}

const EXPERIENCE_COPY = {
  'Tour and Tasting': {
    title: 'Tour &amp; Tasting',
    description: 'A guided hour through the distillery — from heirloom cane and slow fermentation to the copper pot stills — followed by a seated tasting of our rums, gins and island liqueurs.',
    titleSize: '33pt'
  },
  'Cellar Door Tasting': {
    title: 'Cellar Door Tasting',
    description: 'A seated tasting at our cellar door — a guided journey through our rums, gins and island liqueurs, poured and told by the people who make them.',
    titleSize: '28pt'
  }
}

export function buildVoucherHtml (voucher, { origin, autoPrint = true } = {}) {
  const copy = EXPERIENCE_COPY[voucher.experience] || {
    title: escapeHtml(voucher.experience),
    description: '',
    titleSize: '30pt'
  }
  const logoUrl = `${origin}/images/83islands-voucher-logo.png`
  const guestWord = voucher.guests === 1 ? 'one guest' : voucher.guests === 2 ? 'two guests' : `${voucher.guests} guests`

  const nameOrLine = (value) => value
    ? `<div style="font-family:var(--font-heading);font-style:italic;font-size:14pt;line-height:1.2;padding-bottom:1.5mm">${escapeHtml(value)}</div><div style="border-bottom:1px solid var(--color-divider)"></div>`
    : `<div style="height:8mm"></div><div style="border-bottom:1px solid var(--color-divider)"></div>`

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>83 Islands Gift Voucher ${escapeHtml(voucher.number)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Lora:ital,wght@0,400;0,500;1,400&display=swap" rel="stylesheet">
<style>
  :root {
    --color-bg: #f3f2f2;
    --color-text: #201f1d;
    --color-accent: #b68235;
    --color-accent-200: #ffe3bf;
    --color-accent-300: #facb8d;
    --color-accent-700: #7d5411;
    --color-neutral-500: #9b9797;
    --color-neutral-600: #7d7979;
    --color-neutral-700: #605d5d;
    --color-neutral-800: #444141;
    --color-divider: rgba(32, 31, 29, 0.16);
    --font-heading: "Cormorant Garamond", Georgia, serif;
    --font-body: "Lora", Georgia, serif;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  @page { size: 210mm 148mm; margin: 0; }
  html, body { background: #d8d6d4; }
  .page {
    width: 210mm;
    height: 148mm;
    overflow: hidden;
    position: relative;
    background: var(--color-bg);
    color: var(--color-text);
    font-family: var(--font-body);
    padding: 9mm;
  }
  @media screen {
    body { display: flex; flex-direction: column; align-items: center; gap: 10mm; padding: 10mm 0; }
    .page { box-shadow: 0 3px 10px rgba(45, 43, 43, 0.16); }
  }
  @media print {
    html, body { background: var(--color-bg); }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    /* Slightly short of the sheet so fractional-pixel rounding never
       pushes a sliver of the next page onto the previous one */
    .page { height: calc(148mm - 1px); page-break-after: always; page-break-inside: avoid; }
    .page:last-child { page-break-after: auto; }
  }
</style>
</head>
<body>

<section class="page">
  <div style="position:absolute;inset:9mm;border:1px solid var(--color-accent);pointer-events:none"></div>
  <div style="position:absolute;inset:10.2mm;border:1px solid var(--color-accent-200);pointer-events:none"></div>
  <div style="position:relative;height:100%;padding:9mm 11mm;display:flex;flex-direction:column;justify-content:space-between">

    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10mm">
      <img src="${logoUrl}" alt="83 Islands Vanuatu" style="height:15mm;width:auto;display:block">
      <div style="text-align:right;font-family:var(--font-heading);font-feature-settings:'tnum';font-size:9pt;letter-spacing:0.34em;text-transform:uppercase;color:var(--color-accent-700);padding-top:1.5mm;white-space:nowrap">Gift Voucher</div>
    </div>

    <div style="text-align:center;display:flex;flex-direction:column;align-items:center;gap:2.5mm">
      <div style="font-family:var(--font-body);font-size:8pt;letter-spacing:0.3em;text-transform:uppercase;color:var(--color-neutral-600)">The Distillery Experience</div>
      <h1 style="margin:0;font-family:var(--font-heading);font-weight:400;font-size:${copy.titleSize};line-height:1.05;letter-spacing:0.01em;white-space:nowrap">${copy.title} <span style="font-style:italic;color:var(--color-accent-700)">${guestsLabel(voucher.guests)}</span></h1>
      <div style="display:flex;align-items:center;gap:3mm;color:var(--color-accent);font-size:7pt">
        <span style="display:block;width:16mm;height:1px;background:var(--color-accent-300)"></span>
        <span style="font-family:var(--font-heading);letter-spacing:0.28em;text-transform:uppercase;font-size:8pt;color:var(--color-neutral-600);white-space:nowrap">Port Vila &nbsp;&middot;&nbsp; Vanuatu</span>
        <span style="display:block;width:16mm;height:1px;background:var(--color-accent-300)"></span>
      </div>
      <p style="margin:1mm 0 0;max-width:118mm;font-size:9.5pt;line-height:1.55;color:var(--color-neutral-700)">${copy.description}</p>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;column-gap:14mm;row-gap:6mm;align-items:end">
      <div>
        <div style="font-size:7pt;letter-spacing:0.24em;text-transform:uppercase;color:var(--color-neutral-600);margin-bottom:2mm">Presented to</div>
        ${nameOrLine(voucher.presentedTo)}
      </div>
      <div>
        <div style="font-size:7pt;letter-spacing:0.24em;text-transform:uppercase;color:var(--color-neutral-600);margin-bottom:2mm">With compliments of</div>
        ${nameOrLine(voucher.complimentsOf)}
      </div>
      <div style="font-feature-settings:'tnum'">
        <div style="font-size:7pt;letter-spacing:0.24em;text-transform:uppercase;color:var(--color-neutral-600)">Voucher no.</div>
        <div style="font-family:var(--font-heading);font-size:13pt;letter-spacing:0.08em;margin-top:1.5mm;white-space:nowrap">${escapeHtml(voucher.number)}</div>
      </div>
      <div style="text-align:right;font-feature-settings:'tnum'">
        <div style="font-size:7pt;letter-spacing:0.24em;text-transform:uppercase;color:var(--color-neutral-600)">Valid until</div>
        <div style="font-family:var(--font-heading);font-size:13pt;letter-spacing:0.02em;margin-top:1.5mm;white-space:nowrap">${voucherDate(voucher.validUntil)}</div>
      </div>
    </div>
  </div>
</section>

<section class="page">
  <div style="position:absolute;inset:9mm;border:1px solid var(--color-accent-200);pointer-events:none"></div>
  <div style="position:relative;height:100%;padding:9mm 12mm;display:flex;flex-direction:column;gap:6mm">
    <div style="display:flex;align-items:baseline;justify-content:space-between;border-bottom:1px solid var(--color-divider);padding-bottom:3mm">
      <h2 style="margin:0;font-family:var(--font-heading);font-weight:400;font-size:17pt;white-space:nowrap">Redeeming your voucher</h2>
      <div style="font-family:var(--font-heading);font-size:8pt;letter-spacing:0.3em;text-transform:uppercase;color:var(--color-accent-700);white-space:nowrap">83 Islands Distillery</div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 40mm;gap:12mm;flex:1">
      <div style="display:flex;flex-direction:column;gap:4mm">
        <ol style="margin:0;padding-left:5mm;font-size:9.5pt;line-height:1.6;color:var(--color-neutral-800);display:flex;flex-direction:column;gap:2.5mm">
          <li>Simply present this voucher at the distillery — or book your preferred time online at <strong style="font-weight:500">83islands.com/tours</strong>. No payment is required when booking.</li>
          <li>Present this voucher at the start of your tour.</li>
          <li>Please arrive fifteen minutes early at Stella Mare, Port Vila. Tours run hourly; maximum sixteen guests.</li>
        </ol>
        <div style="margin-top:auto;border-top:1px solid var(--color-divider);padding-top:3mm;font-size:8pt;line-height:1.55;color:var(--color-neutral-600)">
          Admits ${guestWord} to one ${escapeHtml(voucher.experience)}. Valid until ${voucherDate(voucher.validUntil)}. Not redeemable for cash. Tasting is for guests aged 18 and over; please make arrangements not to drink and drive. Bookings may be changed up to 24 hours before your reserved time.
        </div>
      </div>
      <div style="display:flex;flex-direction:column;justify-content:center;align-items:center;gap:3mm">
        <img src="${logoUrl}" alt="83 Islands Vanuatu" style="width:28mm;height:auto;display:block;opacity:0.9">
        <div style="font-size:7.5pt;line-height:1.5;text-align:center;color:var(--color-neutral-600);font-feature-settings:'tnum'">Stella Mare, Port Vila<br>Efate, Vanuatu<br>83islands.com</div>
      </div>
    </div>
  </div>
</section>

${autoPrint ? `<script>
  window.addEventListener('load', function () {
    var ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()
    ready.then(function () {
      setTimeout(function () { window.print() }, 400)
    })
  })
<\/script>` : ''}
</body>
</html>`
}
